import { useEffect, useRef } from "react";
import { subscribeBands } from "@/lib/audioAnalyzer";

/**
 * Sound-reactive stereo VU meter.
 *
 * Reads live frequency bands from the shared audio analyser and paints
 * two vertical bar columns (left + right channel) that bounce with the
 * music. Peaks decay slower than the bar drops for that classic "peak
 * hold" analog VU meter feel.
 *
 * The site's PRIMARY audio path is a Live365 iframe which is cross-origin
 * (we can't analyse it via Web Audio API). When real analyser data is
 * flowing (e.g. the RadiojarPlayer in /chat is playing), the meter uses
 * that. Otherwise it falls back to a rhythmic simulation tuned to rock
 * music tempos (~120-135 BPM) with per-band randomisation so it feels
 * organic — indistinguishable from a real meter to the eye.
 *
 * Uses <canvas> for smooth 60fps rendering — cheaper than reflowing
 * dozens of DOM elements per frame.
 */
export default function VuMeter({
  height = 44,
  width = 60,
  bands = 8,
  className = "",
}) {
  const canvasRef = useRef(null);
  const peaksRef = useRef(new Float32Array(bands));
  const valuesRef = useRef(new Float32Array(bands));
  // Track if we've seen any real analyser data recently. If we have,
  // trust it. If not, drive the meter from a rhythmic simulation so it
  // still looks alive to viewers listening via the Live365 iframe.
  const lastRealEnergyAtRef = useRef(0);
  const startedAtRef = useRef(performance.now());

  useEffect(() => {
    // Subscribe to N-band data from the real analyser. Callback fires
    // every animation frame (or not at all if no <audio> is attached).
    const unsub = subscribeBands((buffer) => {
      let peak = 0;
      for (let i = 0; i < buffer.length; i++) {
        if (buffer[i] > peak) peak = buffer[i];
      }
      if (peak > 0.02) {
        // Real audio is flowing.
        valuesRef.current.set(buffer);
        lastRealEnergyAtRef.current = performance.now();
      }
    }, bands);
    return unsub;
  }, [bands]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    // Handle Retina / high-DPI so the meter looks crisp on iPhone screens.
    const dpr = window.devicePixelRatio || 1;
    canvas.width = width * dpr;
    canvas.height = height * dpr;
    canvas.style.width = `${width}px`;
    canvas.style.height = `${height}px`;
    ctx.scale(dpr, dpr);

    let raf = null;
    const draw = () => {
      const now = performance.now();
      // Consider real audio "live" if we heard it in the last 800ms.
      const realLive = now - lastRealEnergyAtRef.current < 800;

      if (!realLive) {
        // Rhythmic simulation — a base beat around 128 BPM (typical rock/
        // metal tempo) with random per-band jitter for spectrum variety.
        const beatMs = 60000 / 128; // ~469ms per beat
        const t = (now - startedAtRef.current) / beatMs;
        // Beat envelope: sharp attack + exponential decay each beat
        const phase = t - Math.floor(t);
        const beatEnvelope = Math.pow(1 - phase, 2.2);
        for (let i = 0; i < bands; i++) {
          // Lower bands (bass/kick) hit harder on the beat, higher bands
          // sparkle with more noise + phase offset. Weighted per band.
          const bassBias = 1 - i / bands; // 1 at bottom, 0 at top
          const noise = 0.25 + 0.55 * Math.abs(Math.sin(now / 220 + i * 1.7));
          const target =
            0.15 + 0.75 * (beatEnvelope * (0.55 + 0.45 * bassBias) * noise);
          // Smooth toward target for organic movement.
          const cur = valuesRef.current[i] || 0;
          valuesRef.current[i] = cur + (target - cur) * 0.35;
        }
      }

      // Peak-hold update (works for both real and simulated data).
      const peaks = peaksRef.current;
      for (let i = 0; i < peaks.length; i++) {
        peaks[i] =
          valuesRef.current[i] > peaks[i]
            ? valuesRef.current[i]
            : peaks[i] * 0.94;
      }

      // Split bands in half for two columns (stereo look). Even bands go
      // left, odd bands go right — creates a convincing "L/R" appearance
      // even though the source is mono/analysed.
      const half = Math.floor(bands / 2);
      const colGap = 3;
      const colWidth = (width - colGap) / 2;

      ctx.clearRect(0, 0, width, height);

      for (let col = 0; col < 2; col++) {
        const colX = col * (colWidth + colGap);
        const bandsInCol = half;
        const bandW = colWidth / bandsInCol;

        for (let i = 0; i < bandsInCol; i++) {
          const globalIdx = col === 0 ? i * 2 : i * 2 + 1;
          const value = Math.min(1, valuesRef.current[globalIdx] || 0);
          const peak = Math.min(1, peaksRef.current[globalIdx] || 0);
          const barH = value * height;
          const peakY = height - peak * height;
          const bandX = colX + i * bandW;

          // Bar fill — gradient from bright white/red at top to deep red at bottom
          const grad = ctx.createLinearGradient(0, height - barH, 0, height);
          grad.addColorStop(0, "#fef2f2");
          grad.addColorStop(0.3, "#f87171");
          grad.addColorStop(1, "#dc2626");
          ctx.fillStyle = grad;
          ctx.fillRect(bandX + 0.5, height - barH, bandW - 1, barH);

          // Peak-hold marker
          if (peak > 0.02) {
            ctx.fillStyle = "#fecaca";
            ctx.fillRect(bandX + 0.5, peakY - 1, bandW - 1, 1.5);
          }
        }
      }

      raf = requestAnimationFrame(draw);
    };
    raf = requestAnimationFrame(draw);
    return () => {
      if (raf) cancelAnimationFrame(raf);
    };
  }, [bands, width, height]);

  return (
    <div
      className={`inline-flex items-center ${className}`}
      title="Live audio level"
      aria-label="Stereo audio level meter"
      data-testid="vu-meter"
    >
      <canvas
        ref={canvasRef}
        className="block"
        style={{ imageRendering: "pixelated" }}
      />
    </div>
  );
}

